import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Box, Button, Flex, Portal, Text } from '@chakra-ui/react';

/**
 * Tour guiado da tela: destaca o elemento com `data-tour="<alvo>"` e mostra um
 * balão com o texto do passo. Passo cujo alvo não está na tela (ex.: lista
 * vazia) aparece centralizado, sem destaque.
 *
 * `useTour(chave)` abre sozinho na primeira visita (marca em localStorage) e
 * devolve `abrir` para o botão "Como funciona" reabrir quando quiser.
 */
export interface TourPasso {
  alvo?: string;
  titulo: string;
  texto: React.ReactNode;
}

const PREFIXO = 'tour-visto:';
const MARGEM = 8;
const LARGURA = 340;

function jaViu(chave: string): boolean {
  try {
    return window.localStorage.getItem(PREFIXO + chave) === '1';
  } catch {
    return true; // sem storage: não fica abrindo toda vez
  }
}

function marcarVisto(chave: string) {
  try {
    window.localStorage.setItem(PREFIXO + chave, '1');
  } catch {
    // sem storage: segue sem marcar
  }
}

export function useTour(chave: string, autoAbrir = true) {
  const [aberto, setAberto] = useState(false);
  useEffect(() => {
    if (!autoAbrir || jaViu(chave)) return undefined;
    // espera a tela montar antes de procurar os alvos
    const t = window.setTimeout(() => setAberto(true), 800);
    return () => window.clearTimeout(t);
  }, [chave, autoAbrir]);
  const fechar = useCallback(() => {
    marcarVisto(chave);
    setAberto(false);
  }, [chave]);
  const abrir = useCallback(() => setAberto(true), []);
  return { aberto, abrir, fechar };
}

function caixaDoAlvo(alvo?: string): DOMRect | null {
  if (!alvo) return null;
  const el = document.querySelector(`[data-tour="${alvo}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width || r.height ? r : null;
}

export function Tour({
  passos,
  aberto,
  onFechar,
}: {
  passos: TourPasso[];
  aberto: boolean;
  onFechar: () => void;
}) {
  const [i, setI] = useState(0);
  const [caixa, setCaixa] = useState<DOMRect | null>(null);
  const passo = passos[i];

  useEffect(() => {
    if (aberto) setI(0);
  }, [aberto]);

  useEffect(() => {
    if (!aberto || !passo?.alvo) return;
    document
      .querySelector(`[data-tour="${passo.alvo}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [aberto, passo]);

  useLayoutEffect(() => {
    if (!aberto) return undefined;
    const medir = () => setCaixa(caixaDoAlvo(passo?.alvo));
    medir();
    // acompanha o scroll suave e mudanças de layout
    const id = window.setInterval(medir, 200);
    window.addEventListener('resize', medir);
    window.addEventListener('scroll', medir, true);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir, true);
    };
  }, [aberto, passo]);

  useEffect(() => {
    if (!aberto) return undefined;
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [aberto, onFechar]);

  if (!aberto || !passo) return null;

  const ultimo = i === passos.length - 1;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const largura = Math.min(LARGURA, vw - 32);
  let estiloBalao: React.CSSProperties;
  if (caixa) {
    const abaixo = caixa.bottom + 200 < vh || caixa.top < 220;
    const left = Math.min(Math.max(16, caixa.left), vw - largura - 16);
    estiloBalao = abaixo
      ? { top: Math.min(caixa.bottom + 12, vh - 200), left }
      : { bottom: vh - caixa.top + 12, left };
  } else {
    estiloBalao = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  return (
    <Portal>
      <Box position="fixed" inset={0} zIndex={2000} onClick={onFechar} aria-hidden>
        {caixa ? (
          <Box
            position="fixed"
            top={`${caixa.top - MARGEM}px`}
            left={`${caixa.left - MARGEM}px`}
            w={`${caixa.width + MARGEM * 2}px`}
            h={`${caixa.height + MARGEM * 2}px`}
            borderRadius="14px"
            boxShadow="0 0 0 9999px rgba(15, 23, 42, 0.55)"
            border="2px solid #1B88F5"
            transition="all 0.2s"
            pointerEvents="none"
          />
        ) : (
          <Box position="fixed" inset={0} bg="rgba(15, 23, 42, 0.55)" />
        )}
      </Box>
      <Box
        role="dialog"
        aria-modal="true"
        aria-label={passo.titulo}
        position="fixed"
        zIndex={2001}
        w={`${largura}px`}
        bg="white"
        borderRadius="16px"
        boxShadow="0 20px 50px -12px rgba(15,23,42,0.45)"
        p="16px 18px"
        style={estiloBalao}
      >
        <Text fontSize="10.5px" color="#94A3B8" fontWeight={700} letterSpacing="0.1em">
          PASSO {i + 1} DE {passos.length}
        </Text>
        <Text fontSize="15px" fontWeight={700} color="#0F172A" mt={1}>
          {passo.titulo}
        </Text>
        <Box fontSize="13px" color="#475569" mt={2} lineHeight="1.5">
          {passo.texto}
        </Box>
        <Flex mt={4} gap={2} align="center">
          <Button size="xs" variant="ghost" onClick={onFechar}>
            Pular
          </Button>
          <Box flex={1} />
          {i > 0 && (
            <Button size="xs" variant="outline" onClick={() => setI(i - 1)}>
              Voltar
            </Button>
          )}
          <Button
            size="xs"
            colorScheme="blue"
            onClick={() => (ultimo ? onFechar() : setI(i + 1))}
            autoFocus
          >
            {ultimo ? 'Entendi' : 'Próximo'}
          </Button>
        </Flex>
      </Box>
    </Portal>
  );
}
